'use client';
import { createClient, type InsForgeClient } from '@insforge/sdk';
import type { InsforgeUser, OAuthProvider } from '../types';
import type {
  CreateSessionResponse,
  CreateUserResponse,
  GetPublicAuthConfigResponse,
  ResetPasswordResponse,
  VerifyEmailResponse,
  AuthOptions,
} from '@insforge/shared-schemas';

export interface InsforgeManagerState {
  user: InsforgeUser | null | undefined;
  userId: string | null | undefined;
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
}

export interface InsforgeManagerConfig {
  client: InsForgeClient;
  afterSignInUrl?: string;
  onAuthChange?: (user: InsforgeUser | null) => void;
  onSignIn?: (authToken: string, user: InsforgeUser) => Promise<void>;
  onSignOut?: () => Promise<void>;
  onRefresh?: (authToken: string, user: InsforgeUser) => Promise<void>;
}

export class InsforgeManager {
  // Static private instance
  private static instance: InsforgeManager | null = null;

  // State storage
  private user: InsforgeUser | null | undefined = undefined;
  private isLoaded: boolean = false;
  private isInitializing: boolean = false;
  private sdk: InsForgeClient;
  private listeners: Set<(state: InsforgeManagerState) => void> = new Set();

  // Config
  private config: InsforgeManagerConfig;
  private refreshIntervalRef: NodeJS.Timeout | null = null;

  // Private constructor (prevents external instantiation)
  private constructor(config: InsforgeManagerConfig) {
    this.config = config;
    this.sdk = config.client;
    this.user = undefined;
    this.isLoaded = false;
  }

  // Public access method (Singleton core)
  public static getInstance(config: InsforgeManagerConfig): InsforgeManager {
    // Browser environment: reuse instance (update config but keep state)
    if (typeof window !== 'undefined' && InsforgeManager.instance) {
      InsforgeManager.instance.updateConfig(config);
      if (config.client && InsforgeManager.instance.sdk !== config.client) {
        InsforgeManager.instance.sdk = config.client;
      }
      return InsforgeManager.instance;
    }

    // Server environment or first creation: create new instance
    InsforgeManager.instance = new InsforgeManager(config);
    return InsforgeManager.instance;
  }

  // Clear instance (used for testing or cleanup)
  public static clearInstance() {
    if (InsforgeManager.instance) {
      InsforgeManager.instance.cleanup();
    }
    InsforgeManager.instance = null;
  }

  // Update config (for hot reloads)
  private updateConfig(config: InsforgeManagerConfig) {
    this.config = { ...this.config, ...config };
  }

  // Public initialization method
  // Even if we have initialState (isLoaded=true from cookies), we still need to load full user data from SDK/API
  public async initialize(): Promise<void> {
    // Skip if currently initializing (prevent concurrent calls)
    if (this.isInitializing) {
      return;
    }

    // Mark as initializing to prevent concurrent calls
    this.isInitializing = true;

    try {
      // Even if isLoaded=true (from initialState), we need to load full profile
      // initialState only provides basic user info from cookies for SSR hydration
      // Full profile (with all fields) comes from SDK's getCurrentSession()

      await this.loadAuthState();
    } finally {
      this.isInitializing = false;
    }
  }

  // Get current state
  public getState(): InsforgeManagerState {
    const userId = this.user === undefined ? undefined : this.user === null ? null : this.user.id;
    const isSignedIn = this.user === undefined ? undefined : this.user !== null;

    return {
      user: this.user,
      userId,
      isLoaded: this.isLoaded,
      isSignedIn,
    };
  }

  // Subscription mechanism
  public subscribe(listener: (state: InsforgeManagerState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }

  /**
   * Clean up sensitive auth parameters from URL
   * This is needed because SDK's detectAuthCallback may not run in Next.js
   * when the SDK client is created at module level (before hydration)
   */
  private cleanUrlParams(): void {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const authParams = ['access_token', 'user_id', 'email', 'name', 'csrf_token', 'error'];
    const hasAuthParams = authParams.some((p) => params.has(p));

    if (hasAuthParams) {
      const url = new URL(window.location.href);
      authParams.forEach((p) => url.searchParams.delete(p));
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  // Load auth state
  // Gets session and user data from getCurrentSession()
  // Called after hydration to restore authentication state
  private async loadAuthState(): Promise<{ success: boolean; error?: string }> {
    // Clean up URL params first (before any async operations)
    // This ensures params are cleared even if SDK's detectAuthCallback didn't run
    // (e.g., when SDK client is created at module level in Next.js)
    this.cleanUrlParams();

    try {
      // Get current session (handles backend detection and token refresh)
      // This restores login state from httpOnly cookie or localStorage
      // Session includes user data directly, no separate API call needed
      const {
        data: { session },
      } = await this.sdk.auth.getCurrentSession();

      if (!session) {
        // Not logged in - set user to null
        this.user = null;
        if (this.config.onAuthChange) {
          this.config.onAuthChange(null);
        }
        this.isLoaded = true;
        this.notifyListeners();
        return { success: false, error: 'no_session' };
      }

      // Sync token to server-side cookie (for Next.js SSR support)
      // This ensures the latest access token is available in Next.js API routes
      if (this.config.onRefresh && session.accessToken) {
        try {
          await this.config.onRefresh(session.accessToken, session.user);
        } catch (error: unknown) {
          if (error instanceof Error) {
            console.error('[InsforgeManager] Error syncing token on refresh:', error.message);
          }
        }
      }

      // Use user data from session (no need for separate API call)
      const userData: InsforgeUser = {
        id: session.user.id,
        email: session.user.email,
        profile: session.user.profile,
      };

      this.user = userData;

      if (this.config.onAuthChange) {
        this.config.onAuthChange(userData);
      }
      this.isLoaded = true;
      this.notifyListeners();
      return { success: true };
    } catch (error) {
      // Error during auth - set user to null
      this.user = null;
      if (this.config.onAuthChange) {
        this.config.onAuthChange(null);
      }
      this.isLoaded = true;
      this.notifyListeners();

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed',
      };
    }
  }

  // Helper to handle auth success
  private async handleAuthSuccess(accessToken: string, user?: InsforgeUser): Promise<void> {
    if (user) {
      // Set user first
      this.user = user;

      // Immediately notify listeners so UI updates quickly
      this.notifyListeners();

      // Then execute callbacks (these may be slower, e.g. cookie sync)
      if (this.config.onAuthChange) {
        this.config.onAuthChange(user);
      }

      if (this.config.onSignIn) {
        try {
          await this.config.onSignIn(accessToken, user);
        } catch (error: unknown) {
          if (error instanceof Error) {
            console.error('[InsforgeManager] Error syncing token to cookie:', error.message);
          }
        }
      }
    }
  }

  // Business methods
  public async signIn(
    email: string,
    password: string
  ): Promise<CreateSessionResponse | { error: string; statusCode?: number; errorCode?: string }> {
    const sdkResult = await this.sdk.auth.signInWithPassword({
      email,
      password,
    });

    if (sdkResult.data) {
      await this.handleAuthSuccess(
        sdkResult.data.accessToken || '',
        sdkResult.data.user
          ? {
              id: sdkResult.data.user.id,
              email: sdkResult.data.user.email,
              profile: sdkResult.data.user.profile ?? null,
            }
          : undefined
      );
      return sdkResult.data;
    } else {
      return {
        error: sdkResult.error?.message || 'Invalid email or password',
        statusCode: sdkResult.error?.statusCode,
        errorCode: sdkResult.error?.error,
      };
    }
  }

  public async signUp(
    email: string,
    password: string,
    options?: AuthOptions
  ): Promise<CreateUserResponse | { error: string; statusCode?: number; errorCode?: string }> {
    const sdkResult = await this.sdk.auth.signUp({
      email,
      password,
      options,
    });

    if (sdkResult.data) {
      if (sdkResult.data.requireEmailVerification) {
        return sdkResult.data;
      }
      if (sdkResult.data.accessToken) {
        await this.handleAuthSuccess(
          sdkResult.data.accessToken,
          sdkResult.data.user
            ? {
                id: sdkResult.data.user.id,
                email: sdkResult.data.user.email,
                profile: sdkResult.data.user.profile ?? null,
              }
            : undefined
        );
      }
      return sdkResult.data;
    } else {
      return {
        error: sdkResult.error?.message || 'Sign up failed',
        statusCode: sdkResult.error?.statusCode,
        errorCode: sdkResult.error?.error,
      };
    }
  }

  public async signOut(): Promise<void> {
    await this.sdk.auth.signOut();

    if (this.config.onSignOut) {
      try {
        await this.config.onSignOut();
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error('[InsforgeManager] Error clearing cookie:', error.message);
        }
      }
    }

    if (this.refreshIntervalRef) {
      clearInterval(this.refreshIntervalRef);
      this.refreshIntervalRef = null;
    }

    this.user = null;
    if (this.config.onAuthChange) {
      this.config.onAuthChange(null);
    }

    this.notifyListeners();
  }

  public async updateUser(data: InsforgeUser['profile']): Promise<{ error: string } | null> {
    if (!this.user) {
      return { error: 'No user signed in' };
    }

    if (!data) {
      return { error: 'No profile data provided' };
    }

    const result = await this.sdk.auth.setProfile(data);

    if (result.data) {
      // Update local user state with the values we sent
      const updatedUser: InsforgeUser = {
        id: this.user.id,
        email: this.user.email,
        profile: data,
      };
      this.user = updatedUser;
      if (this.config.onAuthChange) {
        this.config.onAuthChange(updatedUser);
      }
      this.notifyListeners();
      return null;
    }

    const error = result.error as { message?: string } | undefined;
    return { error: error?.message || 'Failed to update user' };
  }

  public async reloadAuth(): Promise<{ success: boolean; error?: string }> {
    return await this.loadAuthState();
  }

  public async resendVerificationEmail(
    email: string,
    options?: AuthOptions
  ): Promise<{ success: boolean; message: string } | null> {
    const sdkResult = await this.sdk.auth.resendVerificationEmail({
      email,
      options,
    });
    return sdkResult.data;
  }

  public async sendResetPasswordEmail(
    email: string
  ): Promise<{ success: boolean; message: string } | null> {
    const sdkResult = await this.sdk.auth.sendResetPasswordEmail({ email });
    return sdkResult.data;
  }

  public async resetPassword(
    token: string,
    newPassword: string
  ): Promise<ResetPasswordResponse | null> {
    const sdkResult = await this.sdk.auth.resetPassword({
      newPassword,
      otp: token,
    });
    return sdkResult.data;
  }

  public async verifyEmail(
    otp: string,
    email?: string
  ): Promise<VerifyEmailResponse | { error: string; statusCode?: number; errorCode?: string }> {
    const sdkResult = await this.sdk.auth.verifyEmail({ otp, email: email || undefined });
    if (sdkResult.data) {
      await this.handleAuthSuccess(
        sdkResult.data.accessToken,
        sdkResult.data.user
          ? {
              id: sdkResult.data.user.id,
              email: sdkResult.data.user.email,
              profile: sdkResult.data.user.profile ?? null,
            }
          : undefined
      );
      return sdkResult.data;
    } else {
      return {
        error: sdkResult.error?.message || 'Failed to verify email',
        statusCode: sdkResult.error?.statusCode,
        errorCode: sdkResult.error?.error,
      };
    }
  }

  public async exchangeResetPasswordToken(
    email: string,
    code: string
  ): Promise<{ token: string; expiresAt?: string } | { error: { message: string } }> {
    const sdkResult = await this.sdk.auth.exchangeResetPasswordToken({ email, code });
    if (sdkResult.data) {
      return sdkResult.data;
    } else {
      return {
        error: {
          message: sdkResult.error?.message || 'Failed to exchange reset password token',
        },
      };
    }
  }

  public async loginWithOAuth(provider: OAuthProvider, redirectTo: string): Promise<void> {
    const sdkResult = await this.sdk.auth.signInWithOAuth({
      provider,
      redirectTo: redirectTo || window.location.origin + (this.config.afterSignInUrl || '/'),
    });
    if (sdkResult.error) {
      throw new Error(sdkResult.error.message);
    }
  }

  public async getPublicAuthConfig(): Promise<GetPublicAuthConfigResponse | null> {
    try {
      const result = await this.sdk.auth.getPublicAuthConfig();
      if (result.data) {
        return result.data;
      } else {
        console.error('[InsforgeManager] Failed to get public auth config:', result.error);
        return null;
      }
    } catch (error) {
      console.error('[InsforgeManager] Failed to get public auth config:', error);
      return null;
    }
  }

  public setUser(user: InsforgeUser | null): void {
    this.user = user;
    if (this.config.onAuthChange) {
      this.config.onAuthChange(user);
    }
    this.notifyListeners();
  }

  // Set initial state from server (for SSR hydration)
  // This is ONLY basic user info from cookies, not full profile
  // Following Clerk's pattern: initialState prevents hydration mismatch
  // but full user data is still loaded via initialize()
  public setInitialState(initialState: {
    user?: InsforgeUser | null;
    userId?: string | null;
  }): void {
    // Only set if we haven't loaded yet (undefined = not touched)
    if (this.user !== undefined) {
      return;
    }

    // Set basic user info from cookie
    // Note: This is lightweight data, full profile will be loaded by initialize()
    if (initialState.userId) {
      this.user = {
        id: initialState.userId,
        email: initialState.user?.email || '',
        profile: initialState.user?.profile || null,
      };
    } else {
      this.user = null;
    }

    // Mark as "loaded" for hydration purposes
    // But we'll still call loadAuthState() to get full profile
    this.isLoaded = true;
    this.notifyListeners();
  }

  public getConfig(): InsforgeManagerConfig {
    return this.config;
  }

  public getSDK(): ReturnType<typeof createClient> {
    return this.sdk;
  }

  /**
   * Get current session with accessToken
   * Used for hosted auth to construct legacy flow redirect URL
   *
   * @returns Session with accessToken and user, or null if not signed in
   */
  public async getSession(): Promise<{
    accessToken: string;
    user: InsforgeUser;
  } | null> {
    const result = await this.sdk.auth.getCurrentSession();
    if (result.data?.session) {
      return {
        accessToken: result.data.session.accessToken,
        user: {
          id: result.data.session.user.id,
          email: result.data.session.user.email,
          profile: result.data.session.user.profile,
        },
      };
    }
    return null;
  }

  // Cleanup
  private cleanup(): void {
    if (this.refreshIntervalRef) {
      clearInterval(this.refreshIntervalRef);
      this.refreshIntervalRef = null;
    }
    this.listeners.clear();
  }
}
